import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { handleApi } from "../../apis";
import { GET_BILLING_CONSUMERS_V4 } from "../../apis/configuration";
import { useSnackbar } from "../../context/SnackbarContext";

const PAGE_LIMIT = 20;

const normalizeConsumer = (c) => ({
  bill_company: c.biller_name || c.company || c.bill_company || "",
  billing_company: c.biller_name || c.billing_company || "",
  logo_link: c.biller_icon || c.logo_link || null,
  consumer_number_masked: c.consumer_number_masked || c.consumer_number || "",
  consumer_number: c.consumer_number || "",
  consumer_name: c.consumer_name || null,
  last_transaction_at: c.last_transaction_at || null,
  total_txns: c.bill_count || c.total_bills || c.txn_count || 0,
});

const useBillingConsumers = ({ customerPhone = null, seedData = null } = {}) => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  const hasSeed = Boolean(seedData?.consumers?.length);

  const [consumers, setConsumers] = useState(
    hasSeed ? seedData.consumers.map(normalizeConsumer) : []
  );
  const [hasNext, setHasNext] = useState(hasSeed ? Boolean(seedData.has_next) : false);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(!hasSeed);

  const cancelToken = useRef();
  const nextIdRef = useRef(hasSeed ? (seedData.next_id ?? null) : null);
  const nextLastTxnAtRef = useRef(hasSeed ? (seedData.next_last_transaction_at || null) : null);

  const fetchConsumers = useCallback(async ({ phone, reset }) => {
    if (!phone) return;

    if (cancelToken.current)
      cancelToken.current.cancel("New request appears, canceling previous request.");
    cancelToken.current = axios.CancelToken.source();

    setIsLoading(true);
    try {
      const params = { customer_phone: phone, limit: PAGE_LIMIT };
      if (!reset && nextIdRef.current != null) params.next_id = nextIdRef.current;
      if (!reset && nextLastTxnAtRef.current) params.next_last_transaction_at = nextLastTxnAtRef.current;

      const data = await handleApi(GET_BILLING_CONSUMERS_V4, params, cancelToken.current, navigate);

      if (data?.success) {
        const incoming = (data.consumers || data.results || []).map(normalizeConsumer);
        setConsumers((prev) => (reset ? incoming : [...prev, ...incoming]));
        setHasNext(Boolean(data.has_next));
        nextIdRef.current = data.next_id != null ? data.next_id : null;
        nextLastTxnAtRef.current = data.next_last_transaction_at || null;
        if (reset) {
          setTotalCount(data.total_count || data.count || 0);
        }
      }
    } catch (error) {
      if (error.message === "New request appears, canceling previous request.") return;
      showSnackbar({ message: error?.response?.data?.detail || error?.response?.data?.message || "Error fetching data", severity: "error" });
    } finally {
      setIsLoading(false);
      setIsInitialLoading(false);
    }
  }, [navigate, showSnackbar]);

  const resetAndFetch = useCallback(() => {
    if (!customerPhone) return;
    setConsumers([]);
    setHasNext(false);
    setIsInitialLoading(true);
    nextIdRef.current = null;
    nextLastTxnAtRef.current = null;
    fetchConsumers({ phone: customerPhone, reset: true });
  }, [customerPhone, fetchConsumers]);

  useEffect(() => {
    if (customerPhone && !hasSeed) resetAndFetch();
  }, [customerPhone]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLoadMore = useCallback(() => {
    if (!hasNext || isLoading) return;
    fetchConsumers({ phone: customerPhone, reset: false });
  }, [hasNext, isLoading, customerPhone, fetchConsumers]);

  return {
    consumers,
    hasNext,
    totalCount,
    isLoading,
    isInitialLoading,
    handleLoadMore,
  };
};

export default useBillingConsumers;
